<template>
  <div class="upload-container">
    <!-- 图片列表（每行最多 5 个） -->
    <div class="image-list">
      <div
        class="image-row"
        v-for="(row, rowIndex) in groupedFileList"
        :key="rowIndex">
        <div
          v-for="(file, colIndex) in row"
          :key="file.uid"
          class="image-item">
          <img :src="file.url" @click="handlePreview(file)" />
          <div class="image-controls">
            <a-button
              class="arrow-btn"
              type="link"
              :disabled="rowIndex * 5 + colIndex === 0"
              @click="moveImage(rowIndex * 5 + colIndex, 'left')">
              <a-icon type="left" />
            </a-button>
            <a-button
              class="arrow-btn"
              type="link"
              :disabled="rowIndex * 5 + colIndex === fileList.length - 1"
              @click="moveImage(rowIndex * 5 + colIndex, 'right')">
              <a-icon type="right" />
            </a-button>
          </div>
          <div class="delete-btn" v-if="!disabled" @click="removeFile(file)">
            <a-icon type="delete" />
          </div>
        </div>
      </div>
    </div>
    <a-upload
      v-model="fileList"
      :disabled="disabled"
      listType="picture-card"
      :fileList="fileList"
      :accept="acceptAttr"
      :remove="removeFile"
      :multiple="true"
      :show-upload-list="false"
      :before-upload="beforeUpload"
      :customRequest="customRequest"
      @preview="handlePreview">
      <div v-if="(maxCount === -1 || maxCount > fileList.length) && !disabled">
        <a-icon :type="loading ? 'loading' : 'plus'" />
        <div v-show="!loading" class="ant-upload-text">上传</div>
      </div>
    </a-upload>

    <!-- 预览弹窗 -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="previewCancel">
      <img v-if="previewFileType === 'img'" alt="example" style="width: 100%" :src="previewImage" />
      <video
        v-else-if="previewFileType === 'video'"
        ref="previewVideo"
        style="width: 100%"
        :src="previewImage"
        :poster="previewImage + '?x-oss-process=video/snapshot,t_0,f_jpg'"
        :controls="true">
        <source :src="previewImage" type="video/mp4" />
      </video>
      <div v-else>
        <div>
          文件暂不支持在线预览, 请点击下载至本地后, 进行查看 <br /> <a :href="previewImage">下载文件</a>
        </div>
      </div>
    </a-modal>
  </div>
</template>
<script>
import { generateFilePath, uploadObject } from '@/api/tool/oss'

export default {
  props: {
    value: [String,Array],
    type: String, // 文件类型, 支持 "img"、"pdf"、"video" 或逗号分隔组合(如 "img,pdf")
    // 最大文件大小, 单位 兆M, 默认 20兆
    maxSize: {
      type: Number,
      default: 20
    },
    /* 文件夹前缀 */
    prefix: {
      type: String,
      default: 'upload'
    },
    // 最大数量, 1 限制1张
    maxCount: {
      type: Number,
      default: 1
    },
    isArrayData: {
      type: Boolean,
      default: false
    },
    disabled:{
      type: Boolean,
      default: false
    }
  },
  created() {
    this.valueChange(this.value)
  },
  data() {
    return {
      fileList: [], // 文件集合
      loading: false, // 加载状态
      previewVisible: false, // 预览弹窗隐藏/显示
      previewImage: '', // 预览文件 url
      previewFileType: '' // 当前预览文件类型: img/video/pdf/other
    }
  },
  computed: {
    // 将 fileList 按每行 5 个进行分组，方便模板中每 5 个换行展示
    groupedFileList() {
      const result = []
      for (let i = 0; i < this.fileList.length; i += 5) {
        result.push(this.fileList.slice(i, i + 5))
      }
      return result
    },
    acceptAttr() {
      const acceptMap = {
        img: 'image/*',
        pdf: '.pdf',
        video: 'video/mp4'
      }
      const accepts = this.getAllowedTypeTokens().map(token => acceptMap[token]).filter(Boolean)
      return accepts.length ? accepts.join(',') : undefined
    }
  },
  methods: {
    // 删除图片
    removeFile(file) {
      this.fileList = this.fileList.filter(item => item.url !== file.url)
      this.$emit('input', this.isArrayData ? this.fileList.map(item => item.url) : this.fileList.map(item => item.url).join(','));
    },
    // 预览按钮点击
    handlePreview(file) {
      this.previewImage = file.url
      this.previewFileType = this.resolvePreviewType(file)
      this.previewVisible = true
    },
    // 上传之前校验
    beforeUpload(file) {
      const allowTypeTokens = this.getAllowedTypeTokens()
      if (allowTypeTokens.length > 0) {
        const isAllowedType = allowTypeTokens.some(token => this.isFileTypeMatch(file, token))
        if (!isAllowedType) {
          this.$message.error(`上传文件类型不支持，请上传 ${this.type} 类型文件`)
          return false
        }
      }
      // 大小校验
      if (file.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`上传文件大小不能超过 ${this.maxSize} MB`)
        return false
      }
    },
    // 解析允许上传的类型，支持以逗号分隔传入多个类型
    getAllowedTypeTokens() {
      if (!this.type) return []
      return this.type.split(',').map(item => item.trim().toLowerCase()).filter(Boolean).map(item => {
        if (item === 'image') return 'img'
        if (item === 'mp4') return 'video'
        return item
      })
    },
    // 判断文件是否匹配目标类型
    isFileTypeMatch(file, typeToken) {
      const fileMime = (file.type || '').toLowerCase()
      const fileName = (file.name || '').toLowerCase()
      const ext = this.getFileExtension(fileName)
      if (typeToken === 'img') {
        return fileMime.startsWith('image/') || ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(ext)
      }
      if (typeToken === 'pdf') {
        return fileMime === 'application/pdf' || ext === '.pdf'
      }
      if (typeToken === 'video') {
        return fileMime === 'video/mp4' || ext === '.mp4'
      }
      return false
    },
    getFileExtension(fileName = '') {
      const idx = fileName.lastIndexOf('.')
      return idx >= 0 ? fileName.substring(idx) : ''
    },
    resolvePreviewType(file) {
      const fileMime = ((file && file.type) || '').toLowerCase()
      const url = ((file && file.url) || '').toLowerCase()
      const ext = this.getFileExtension(url)
      if (fileMime.startsWith('image/') || ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(ext)) {
        return 'img'
      }
      if (fileMime === 'video/mp4' || ext === '.mp4') {
        return 'video'
      }
      if (fileMime === 'application/pdf' || ext === '.pdf') {
        return 'pdf'
      }
      return 'other'
    },
    // 自定义上传请求
    customRequest(fileInfo, index) {
      this.loading = true

      // 文件
      const uploadFile = fileInfo.file

      // 文件夹目录
      const prefix = this.prefix
      const url = this.buildOssUrl(uploadFile.name, prefix)

      // oss 上传
      uploadObject(url, uploadFile).then(res => {
        this.fileList.push(this.createAntFileObj(res))
        this.$message.success('上传成功')
        this.loading = false
        this.$emit('input', this.isArrayData ? this.fileList.map(item => item.url) : this.fileList.map(item => item.url).join(','))
      })
    },
    // 构建 oss url
    buildOssUrl(fileName, prefix) {
      // 获取文件后缀
      const suffix = fileName.substring(fileName.lastIndexOf('.'))
      // 生成文件名称
      const filePath = generateFilePath(prefix, suffix)
      return filePath
    },
    // 文件随机 uid
    getUidRandom() {
      return Math.round(Math.random() * 80 + 20)
    },
    // 预览关闭
    previewCancel() {
      this.previewVisible = false
      // 视频暂停播放
      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.pause()
      }
    },
    valueChange(newValue) {
      if (!newValue) {
        this.fileList = []
        return
      }
      if (typeof newValue === 'string') {
        const imgUrls = newValue.split(',')
        // 判断 url 是否存在, 不存在进行添加
        imgUrls.forEach(imgUrl => {
          for (let i = 0; i < this.fileList.length; i++) {
            const item = this.fileList[i]
            if (item.url === imgUrl) {
              return
            }
          }
          this.fileList.push(this.createAntFileObj(imgUrl))
        })
      } else {
        this.fileList = newValue.map(item => (this.createAntFileObj(item)))
      }
    },
    // 创建 ant fileList 的对象
    createAntFileObj(ossUrl) {
      return {
        status: 'done',
        url: ossUrl,
        uid: this.getUidRandom(),
        name: ossUrl,
        thumbUrl: this.getThumbUrl(ossUrl)
      }
    },
    // 预览图片
    getThumbUrl(ossUrl) {
      switch (this.type) {
        // 视频, 使用 oss 封面图功能, 文档: https://help.aliyun.com/zh/oss/user-guide/video-snapshots
        case 'video':
          return ossUrl + '?x-oss-process=video/snapshot,t_0,f_jpg,w_0,h_0,m_fast'
        // 图片/默认
        case 'img':
        default:
          return ossUrl
      }
    },
    // 添加移动图片的方法
    moveImage(index, direction) {
      if (direction === 'left' && index > 0) {
        const temp = this.fileList[index]
        this.$set(this.fileList, index, this.fileList[index - 1])
        this.$set(this.fileList, index - 1, temp)
      } else if (direction === 'right' && index < this.fileList.length - 1) {
        const temp = this.fileList[index]
        this.$set(this.fileList, index, this.fileList[index + 1])
        this.$set(this.fileList, index + 1, temp)
      }
      // 触发更新
      this.$emit('input', this.isArrayData ? this.fileList.map(item => item.url) : this.fileList.map(item => item.url).join(','))
    }
  },
  watch: {
    // 监听上级 value 改变
    value(newValue) {
      this.valueChange(newValue)
    }
  }
}
</script>

<style lang="less" scoped>
.image-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.image-row {
  display: flex;
  gap: 8px;
}
.upload-container {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 8px;
}
.image-item {
  position: relative;
  width: 104px;
  height: 104px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 4px;
  padding-top: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // 删除按钮默认隐藏
  .delete-btn {
    display: none;
    position: absolute;
    top: 1px;
    right: 1px;
    color: #ff4d4f;  // 红色
    cursor: pointer;
    background: rgba(255, 255, 255, 0.8);  // 白色半透明背景
    &:hover {
      color: #ff7875;  // 红色悬浮效果
      background: rgba(255, 255, 255, 0.95);
    }
  }

  // 悬浮时显示删除按钮
  &:hover .delete-btn {
    display: block;
  }

  .image-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    background: transparent;  // 透明背景
    padding: 4px;

    .arrow-btn {
      color: #1890ff;  // 蓝色
      padding: 0 4px;
      height: 24px;
      background: rgba(255, 255, 255, 0.8);  // 白色半透明背景
      border-radius: 4px;

      &[disabled] {
        color: rgba(0, 0, 0, 0.25);  // 禁用状态颜色
        background: rgba(255, 255, 255, 0.6);
      }

      &:hover:not([disabled]) {
        color: #40a9ff;  // 蓝色悬浮效果
        background: rgba(255, 255, 255, 0.95);
      }
    }
  }
}
</style>
